import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContent } from "@/lib/cms";

const features = [
  "Shared Team Inbox",
  "Multi-Agent Access",
  "Chatbot Builder",
  "Flow Builder",
  "Dedicated Support",
  "WhatsApp API Assistance",
];

const plans = [
  {
    name: "Monthly",
    billed: "Billed Monthly",
    price: "₹2,499",
    suffix: "/month",
    note: "+ GST",
    total: null,
    accent: "blue",
    cta: "Start monthly",
  },
  {
    name: "Quarterly",
    billed: "Billed Quarterly",
    price: "₹1,999",
    suffix: "/month",
    note: "+ GST",
    total: "Total ₹5,997 + GST every 3 months",
    accent: "green",
    cta: "Choose quarterly",
  },
  {
    name: "Half-Yearly",
    billed: "Billed Half-Yearly",
    price: "₹1,650",
    suffix: "/month",
    note: "+ GST",
    total: "Total ₹9,900 + GST every 6 months",
    accent: "blue",
    cta: "Choose half-yearly",
  },
  {
    name: "Yearly",
    billed: "Billed Yearly",
    price: "₹1,500",
    suffix: "/month",
    note: "+ GST · Best value",
    total: "Total ₹18,000 + GST annually",
    accent: "green",
    cta: "Get yearly",
    popular: true,
  },
];

export const Pricing = ({ onSelectPlan }) => {
  const { data } = useContent("pricing", {});
  const plansList = data.plans && data.plans.length ? data.plans : plans;
  const featuresList = data.features && data.features.length ? data.features : features;
  const eyebrow = data.eyebrow || "Transparent Indian pricing";
  const heading = data.heading || "One plan. All features. Pay how you prefer.";
  const subhead = data.subhead || "Every Wappit plan ships with the full platform — Shared Team Inbox, Multi-Agent Access, Chatbot & Flow Builder, Dedicated Support and WhatsApp API Assistance.";

  return (
    <section id="pricing" className="py-20 sm:py-28 bg-slate-50 relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">{eyebrow}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {heading}
          </h2>
          <p className="mt-4 text-lg text-slate-600">{subhead}</p>
          <p className="mt-2 text-xs text-slate-500">WhatsApp conversation charges billed by Meta separately at-cost (no markup).</p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {plansList.map((p, i) => {
            const isGreen = p.accent === "green";
            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`pricing-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`relative rounded-3xl p-6 flex flex-col ${
                  p.popular
                    ? "bg-slate-900 text-white shadow-2xl ring-1 ring-slate-900"
                    : "bg-white border border-slate-200 shadow-sm"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1 rounded-full bg-emerald-500 text-white">
                    <Sparkles className="h-3 w-3" /> Best value
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <h3 className={`text-lg font-bold ${p.popular ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "Outfit, sans-serif" }}>{p.name}</h3>
                  <span className={`h-8 w-8 rounded-xl flex items-center justify-center ${
                    isGreen ? "bg-emerald-100 text-emerald-700" : p.popular ? "bg-white/10 text-emerald-300" : "bg-blue-100 text-blue-700"
                  }`}>★</span>
                </div>
                <p className={`mt-1 text-xs ${p.popular ? "text-slate-300" : "text-slate-500"}`}>{p.billed}</p>

                <div className="mt-5 flex items-end gap-1">
                  <span className={`text-4xl font-extrabold ${p.popular ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                    {p.price}
                  </span>
                  <span className={`mb-1.5 text-sm ${p.popular ? "text-slate-300" : "text-slate-500"}`}>{p.suffix}</span>
                </div>
                <div className={`mt-1 text-xs font-semibold ${p.popular ? "text-emerald-300" : "text-emerald-700"}`}>{p.note}</div>
                {p.total && (
                  <div className={`mt-1 text-[11px] ${p.popular ? "text-slate-300" : "text-slate-500"}`} data-testid={`pricing-total-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {p.total}
                  </div>
                )}

                <Button
                  onClick={() => onSelectPlan?.(p.name)}
                  data-testid={`pricing-cta-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`mt-5 rounded-full h-11 font-semibold ${
                    p.popular
                      ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                      : isGreen
                      ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                >
                  {p.cta || `Choose ${p.name}`}
                </Button>

                <div className={`mt-3 rounded-xl px-3 py-2 text-center ${p.popular ? "bg-white/10 ring-1 ring-white/15" : "bg-slate-50 ring-1 ring-slate-100"}`}>
                  <div className={`text-[11px] ${p.popular ? "text-slate-300" : "text-slate-500"}`}>Ready to activate your WhatsApp Business API?</div>
                  <Link
                    to="/whatsapp-onboarding"
                    data-testid={`pricing-onboarding-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`mt-1 inline-flex items-center gap-1 text-xs font-bold ${p.popular ? "text-emerald-300 hover:text-emerald-200" : "text-emerald-700 hover:text-emerald-800"}`}
                  >
                    Complete Onboarding →
                  </Link>
                </div>

                <ul className="mt-6 space-y-2.5">
                  {featuresList.map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${p.popular ? "text-slate-200" : "text-slate-700"}`}>
                      <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${p.popular ? "text-emerald-400" : "text-emerald-500"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm text-slate-500">
          Need volume / enterprise pricing or white-label?{" "}
          <button
            onClick={() => onSelectPlan?.("Enterprise")}
            data-testid="pricing-talk-to-expert"
            className="font-semibold text-blue-700 hover:text-blue-800"
          >
            Talk to an expert →
          </button>
        </p>
      </div>
    </section>
  );
};
